import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import FilledInfoCard from "./InfoCard";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import SimpleModal from "components/SimpleModal";

import { useTranslation } from "react-i18next";
import api from "services/api";
import { clearAuthStorage } from "utils/auth";

import bgImage from "assets/images/bg-user-info.jpg";

function UserHero({ user, scrollToPricing, scrollToAppList }) {
  const { t } = useTranslation();

  const [osKey, setOsKey] = useState("unknown");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmEmailLoading, setConfirmEmailLoading] = useState(false);
  const [confirmEmailMessage, setConfirmEmailMessage] = useState("");
  const [confirmEmailError, setConfirmEmailError] = useState("");
  const [showDevicesModal, setShowDevicesModal] = useState(false);
  const [devices, setDevices] = useState([]);
  const [deviceLimit, setDeviceLimit] = useState(null);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState("");
  const [disconnectingDeviceId, setDisconnectingDeviceId] = useState("");

  // UserHero 只在 UserInfo 页面使用，用户信息由父组件传入
  const profile = user || null;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const nav = window.navigator || {};
    const ua = (nav.userAgent || nav.vendor || "").toLowerCase();
    let key = "unknown";

    // iOS 设备（iPhone / iPod / 旧版 iPad）
    const isIOS = /iphone|ipad|ipod/.test(ua);

    // Android 设备
    const isAndroid = /android/.test(ua);

    // iPadOS 13+ 会把 UA 伪装成 Macintosh，但 platform=MacIntel 且为触摸屏
    const isModernIPad =
      !isIOS &&
      !isAndroid &&
      nav.platform === "MacIntel" &&
      typeof nav.maxTouchPoints === "number" &&
      nav.maxTouchPoints > 1;

    if (isIOS || isModernIPad) {
      key = "ios";
    } else if (isAndroid) {
      key = "android";
    } else if (/windows nt/.test(ua)) {
      key = "windows";
    } else if (/macintosh/.test(ua)) {
      key = "macos";
    } else if (/linux/.test(ua)) {
      key = "linux";
    }

    setOsKey(key);
  }, []);

  const effectiveEmail = profile?.username || profile?.email || "";
  const levelExpireTime = profile?.level_expire_time || null;

  const planDescription = levelExpireTime
    ? t("userInfo.planActiveUntil", { date: levelExpireTime })
    : t("userInfo.planInactive");

  const osLabelMap = {
    windows: t("userInfo.os.windows"),
    macos: t("userInfo.os.macos"),
    android: t("userInfo.os.android"),
    ios: t("userInfo.os.ios"),
    linux: t("userInfo.os.linux"),
    unknown: t("userInfo.os.unknown"),
  };

  const detectedOSLabel = osLabelMap[osKey] || osLabelMap.unknown;

  const osDownloadRouteMap = {
    windows: "/client/windows/",
    macos: "/client/macos/",
    android: "/client/android/",
    ios: "/client/ios/",
    linux: "/client/windows/",
    unknown: "/clients/",
  };

  const detectedOSRoute = osDownloadRouteMap[osKey] || osDownloadRouteMap.unknown;

  const fetchDevices = () => {
    if (!profile) return;

    let mounted = true;
    setDevicesLoading(true);
    setDevicesError("");

    api
      .get("auth/devices/")
      .then((response) => {
        if (!mounted) return;
        setDevices(Array.isArray(response?.data?.devices) ? response.data.devices : []);
        setDeviceLimit(response?.data?.deviceLimit ?? null);
      })
      .catch((err) => {
        if (!mounted) return;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearAuthStorage();
          window.location.href = "/signin/";
          return;
        }
        setDevicesError(t("userInfo.devices.fetchError"));
      })
      .finally(() => {
        if (mounted) {
          setDevicesLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  };

  useEffect(() => {
    return fetchDevices();
  }, [profile, t]);

  const resetPasswordForm = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordLoading(false);
    setPasswordMessage("");
    setPasswordError("");
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    resetPasswordForm();
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    if (!oldPassword || !newPassword || !confirmPassword) {
      setPasswordError(t("userInfo.passwordModal.missingFields"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("userInfo.passwordModal.passwordMismatch"));
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post("auth/change-password/", {
        old_password: oldPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setPasswordMessage(t("userInfo.passwordModal.success"));
      setTimeout(() => {
        clearAuthStorage();
        window.location.href = "/signin/";
      }, 1200);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.non_field_errors?.[0] ||
        err?.message ||
        t("userInfo.passwordModal.submitError");
      setPasswordError(detail);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSendConfirmEmail = async () => {
    setConfirmEmailMessage("");
    setConfirmEmailError("");
    setConfirmEmailLoading(true);

    try {
      const response = await api.post("auth/send-confirm-email/", {});
      setConfirmEmailMessage(response?.data?.detail || t("userInfo.confirmEmail.success"));
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || t("userInfo.confirmEmail.submitError");
      setConfirmEmailError(detail);
    } finally {
      setConfirmEmailLoading(false);
    }
  };

  const formatDeviceTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDeviceLabel = (device) => {
    const name = device?.deviceName || t("userInfo.devices.unknownDevice");
    const platform = device?.platform ? String(device.platform).toUpperCase() : "";
    return platform ? `${name} · ${platform}` : name;
  };

  const formatAppVersion = (version) => {
    const raw = String(version || "").trim();
    if (!raw) return t("userInfo.devices.unknownVersion");
    return raw.replace(/\s*\(.+\)\s*$/, "");
  };

  const handleDisconnectDevice = async (device) => {
    if (!device?.sessionID) return;

    setDisconnectingDeviceId(device.sessionID);
    try {
      await api.post(`auth/devices/${device.sessionID}/disconnect/`, {});
      setDevices((current) => current.filter((item) => item.sessionID !== device.sessionID));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearAuthStorage();
        window.location.href = "/signin/";
        return;
      }
      setDevicesError(t("userInfo.devices.disconnectError"));
    } finally {
      setDisconnectingDeviceId("");
    }
  };

  const latestDevice = devices[0] || null;

  return (
    <>
      <MKBox
        minHeight="55vh"
        width="100%"
        sx={({ breakpoints }) => ({
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
          paddingTop: "3rem",
          paddingBottom: "3rem",
          [breakpoints.down("md")]: {
            minHeight: "65vh",
            paddingTop: "6rem",
            paddingBottom: "6rem",
          },
        })}
      >
        <Container>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: "center", md: "left" } }}>
              <MKTypography variant="body1" color="white" opacity={0.8} mt={1} mb={1}>
                {effectiveEmail ? t("userInfo.welcomeUser") : t("userInfo.welcomeGuest")}
              </MKTypography>
              {effectiveEmail && (
                <MKTypography variant="h6" color="white" fontWeight="bold" mt={-0.5} mb={2}>
                  {effectiveEmail}
                </MKTypography>
              )}
              <MKBox
                mt={4}
                display="flex"
                flexWrap="wrap"
                justifyContent={{ xs: "center", md: "flex-start" }}
                gap={1.5}
              >
                {profile?.mail_confirmed === false && (
                  <MKButton
                    variant="outlined"
                    color="white"
                    size="small"
                    sx={{ fontSize: "0.75rem", padding: "4px 10px", lineHeight: 1 }}
                    onClick={handleSendConfirmEmail}
                    disabled={confirmEmailLoading}
                  >
                    {confirmEmailLoading
                      ? t("userInfo.confirmEmail.sending")
                      : t("userInfo.verifyEmail")}
                  </MKButton>
                )}

                <MKButton
                  variant="outlined"
                  color="white"
                  size="small"
                  sx={{ fontSize: "0.75rem", padding: "4px 10px", lineHeight: 1 }}
                  onClick={() => setShowPasswordModal(true)}
                >
                  {t("userInfo.changePassword")}
                </MKButton>
              </MKBox>
              {(confirmEmailMessage || confirmEmailError) && (
                <MKTypography
                  variant="button"
                  color={confirmEmailError ? "error" : "success"}
                  display="block"
                  mt={1.5}
                >
                  {confirmEmailError || confirmEmailMessage}
                </MKTypography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FilledInfoCard
                    variant="contained"
                    color="info"
                    sx={{ backgroundColor: "#d5d7da" }} // darker card background
                    description={planDescription}
                    isPlanCard
                    planExpireAt={levelExpireTime}
                    noPlanMessage={t("userInfo.planInactive")}
                    action={{
                      type: "scroll",
                      onClick: scrollToPricing,
                      label: t("userInfo.viewPlans"),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FilledInfoCard
                    variant="contained"
                    color="dark"
                    sx={{ backgroundColor: "#d5d7da" }} // same darker background
                    description={
                      <>
                        {t("userInfo.downloadClientDescription", {
                          os: detectedOSLabel,
                        })}{" "}
                        <a
                          href={detectedOSRoute}
                          style={{
                            textDecoration: "underline",
                            color: "#007bff",
                            cursor: "pointer",
                          }}
                        >
                          {t("userInfo.downloadClientLinkLabel")}
                        </a>
                      </>
                    }
                    action={{
                      type: "scroll",
                      onClick: scrollToAppList,
                      label: t("userInfo.downloadOtherClientsLabel"),
                      icon: "arrow_downward",
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MKBox
                    borderRadius="xl"
                    px={3}
                    py={2}
                    sx={{
                      backgroundColor: "rgba(17, 24, 39, 0.75)",
                      boxShadow: "0 18px 45px rgba(15, 23, 42, 0.75)",
                    }}
                  >
                    <MKBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1.5}
                    >
                      <MKBox minWidth={0}>
                        <MKTypography variant="h6" color="white" fontWeight="bold">
                          {t("userInfo.devices.title")}
                        </MKTypography>
                        {devicesLoading && (
                          <MKTypography variant="body2" color="white" opacity={0.78}>
                            {t("userInfo.devices.loading")}
                          </MKTypography>
                        )}
                        {!devicesLoading && devicesError && (
                          <MKTypography variant="body2" color="error">
                            {devicesError}
                          </MKTypography>
                        )}
                        {!devicesLoading && !devicesError && (
                          <MKTypography
                            variant="body2"
                            color="white"
                            opacity={0.78}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {devices.length > 0
                              ? t("userInfo.devices.summaryWithLatest", {
                                  count: devices.length,
                                  limit: deviceLimit || "-",
                                  device: formatDeviceLabel(latestDevice),
                                  version: formatAppVersion(latestDevice?.appVersion),
                                })
                              : t("userInfo.devices.empty")}
                          </MKTypography>
                        )}
                      </MKBox>
                      <MKButton
                        variant="outlined"
                        color="white"
                        size="small"
                        onClick={() => setShowDevicesModal(true)}
                        disabled={devicesLoading || Boolean(devicesError) || devices.length === 0}
                        sx={{ flexShrink: 0, fontSize: "0.75rem", padding: "4px 10px" }}
                      >
                        {t("userInfo.devices.manage")}
                      </MKButton>
                    </MKBox>
                  </MKBox>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <SimpleModal
        open={showPasswordModal}
        title={t("userInfo.passwordModal.title")}
        showCancel={false}
        showConfirm={false}
        onClose={closePasswordModal}
      >
        <MKBox component="form" onSubmit={handleChangePassword} autoComplete="off">
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={effectiveEmail}
            readOnly
            hidden
          />
          <MKBox mb={2}>
            <MKInput
              type="password"
              label={t("userInfo.passwordModal.oldPassword")}
              fullWidth
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              id="current-password"
              name="current_password"
              autoComplete="current-password"
              InputLabelProps={{ shrink: true }}
            />
          </MKBox>
          <MKBox mb={2}>
            <MKInput
              type="password"
              label={t("userInfo.passwordModal.newPassword")}
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              id="new-password"
              name="new_password"
              autoComplete="new-password"
              InputLabelProps={{ shrink: true }}
            />
          </MKBox>
          <MKBox mb={2}>
            <MKInput
              type="password"
              label={t("userInfo.passwordModal.confirmPassword")}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              id="confirm-new-password"
              name="confirm_new_password"
              autoComplete="new-password"
              InputLabelProps={{ shrink: true }}
            />
          </MKBox>
          {passwordError && (
            <MKTypography variant="button" color="error" display="block" mb={1}>
              {passwordError}
            </MKTypography>
          )}
          {passwordMessage && (
            <MKTypography variant="button" color="success" display="block" mb={1}>
              {passwordMessage}
            </MKTypography>
          )}
          <MKBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <MKButton variant="gradient" color="dark" onClick={closePasswordModal}>
              {t("userInfo.passwordModal.cancel")}
            </MKButton>
            <MKButton variant="gradient" color="info" type="submit" disabled={passwordLoading}>
              {passwordLoading
                ? t("userInfo.passwordModal.submitting")
                : t("userInfo.passwordModal.submit")}
            </MKButton>
          </MKBox>
        </MKBox>
      </SimpleModal>

      <SimpleModal
        open={showDevicesModal}
        title={t("userInfo.devices.modalTitle")}
        showCancel={false}
        showConfirm={false}
        onClose={() => setShowDevicesModal(false)}
      >
        <MKBox>
          <MKTypography variant="body2" color="text" mb={2}>
            {t("userInfo.devices.modalSummary", {
              count: devices.length,
              limit: deviceLimit || "-",
            })}
          </MKTypography>
          <MKBox display="grid" gap={1.25}>
            {devices.map((device) => (
              <MKBox
                key={device.sessionID}
                px={2}
                py={1.5}
                borderRadius="lg"
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.1)",
                  backgroundColor: "rgba(248, 250, 252, 0.9)",
                }}
              >
                <MKBox
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                >
                  <MKBox minWidth={0}>
                    <MKTypography
                      variant="button"
                      color="dark"
                      fontWeight="bold"
                      display="block"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDeviceLabel(device)}
                    </MKTypography>
                    <MKTypography variant="caption" color="text" display="block">
                      {t("userInfo.devices.lastSeen", {
                        time: formatDeviceTime(device.lastSeenAt),
                      })}
                    </MKTypography>
                    <MKTypography variant="caption" color="text" display="block">
                      {t("userInfo.devices.expiresAt", {
                        time: formatDeviceTime(device.expiresAt),
                      })}
                    </MKTypography>
                  </MKBox>
                  <MKTypography
                    variant="caption"
                    color="info"
                    fontWeight="bold"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {formatAppVersion(device.appVersion)}
                  </MKTypography>
                </MKBox>
                <MKBox display="flex" justifyContent="flex-end" mt={1.25}>
                  <MKButton
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleDisconnectDevice(device)}
                    disabled={disconnectingDeviceId === device.sessionID}
                    sx={{ fontSize: "0.75rem", padding: "4px 10px" }}
                  >
                    {disconnectingDeviceId === device.sessionID
                      ? t("userInfo.devices.disconnecting")
                      : t("userInfo.devices.disconnect")}
                  </MKButton>
                </MKBox>
              </MKBox>
            ))}
          </MKBox>
        </MKBox>
      </SimpleModal>
    </>
  );
}

UserHero.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    username: PropTypes.string,
  }),
  scrollToPricing: PropTypes.func,
  scrollToAppList: PropTypes.func,
};

export default UserHero;
